import type { ReactNode } from "react";

import { Box, type SxProps, type Theme } from "@mui/material";

import { FERNIE_FACE_VIEWBOX, FERNIE_SPROUT_PATH, FERNIE_TILE_PATH } from "~/components/fernie/fernieArt";
import { clampMarkSize, type FernieFaceState } from "~/components/fernie/fernieMarkRules";
import { fernieColors } from "~/themes/fernieBrand";

const FACE_ASPECT = 58 / 48;
const EYE_GLOW = `drop-shadow(0 0 2.5px ${fernieColors.led})`;

interface FaceExpression {
	sprout: string;
	eyes: ReactNode;
	glow: boolean;
	mouth?: ReactNode;
}

const EXPRESSIONS: Record<FernieFaceState, FaceExpression> = {
	done: {
		eyes: (
			<>
				<path
					d="M14.5 23 Q17 19.5 19.5 23"
					stroke={fernieColors.led}
					strokeWidth="2.6"
					strokeLinecap="round"
					fill="none"
				/>
				<path
					d="M28.5 23 Q31 19.5 33.5 23"
					stroke={fernieColors.led}
					strokeWidth="2.6"
					strokeLinecap="round"
					fill="none"
				/>
			</>
		),
		glow: true,
		mouth: (
			<path
				d="M17.5 31.5 Q24 37.5 30.5 31.5"
				stroke={fernieColors.led}
				strokeWidth="2.6"
				strokeLinecap="round"
				fill="none"
			/>
		),
		sprout: fernieColors.led
	},
	listening: {
		eyes: (
			<>
				<rect x="14.5" y="18" width="5" height="8.5" rx="2.5" fill={fernieColors.led} />
				<rect x="28.5" y="18" width="5" height="8.5" rx="2.5" fill={fernieColors.led} />
			</>
		),
		glow: true,
		mouth: (
			<path d="M19 33 Q24 36.5 29 33" stroke={fernieColors.led} strokeWidth="2.4" strokeLinecap="round" fill="none" />
		),
		sprout: fernieColors.led
	},
	rest: {
		eyes: (
			<>
				<rect x="14.5" y="19" width="5" height="7" rx="2.5" fill={fernieColors.led} />
				<rect x="28.5" y="19" width="5" height="7" rx="2.5" fill={fernieColors.led} />
			</>
		),
		glow: true,
		mouth: (
			<path d="M19 33 Q24 36.5 29 33" stroke={fernieColors.led} strokeWidth="2.4" strokeLinecap="round" fill="none" />
		),
		sprout: fernieColors.led
	},
	straight: {
		eyes: (
			<>
				<rect x="14.5" y="22" width="5" height="2.6" rx="1.3" fill={fernieColors.dim} />
				<rect x="28.5" y="22" width="5" height="2.6" rx="1.3" fill={fernieColors.dim} />
			</>
		),
		glow: false,
		sprout: fernieColors.dim
	},
	thinking: {
		eyes: (
			<>
				<rect x="14.5" y="20" width="5" height="5" rx="2.5" fill={fernieColors.led} />
				<rect x="28.5" y="20" width="5" height="5" rx="2.5" fill={fernieColors.led} />
			</>
		),
		glow: true,
		mouth: <circle cx="24" cy="34" r="1.4" fill={fernieColors.led} opacity="0.7" />,
		sprout: fernieColors.led
	},
	working: {
		eyes: (
			<>
				<rect x="14.5" y="22" width="5" height="3.6" rx="1.8" fill={fernieColors.led} />
				<rect x="28.5" y="22" width="5" height="3.6" rx="1.8" fill={fernieColors.led} />
			</>
		),
		glow: true,
		mouth: <path d="M20 33.5 H28" stroke={fernieColors.led} strokeWidth="2.4" strokeLinecap="round" fill="none" />,
		sprout: fernieColors.led
	}
};

interface FernieFaceProps {
	state?: FernieFaceState;
	size?: number;
	decorative?: boolean;
	title?: string;
	sx?: SxProps<Theme>;
}

const FernieFace = ({ state = "rest", size = 40, decorative = false, title = "fernie", sx }: FernieFaceProps) => {
	const width = clampMarkSize("face", size);
	const expression = EXPRESSIONS[state];

	return (
		<Box
			component="svg"
			role="img"
			aria-hidden={decorative || undefined}
			aria-label={decorative ? undefined : title}
			viewBox={FERNIE_FACE_VIEWBOX}
			sx={{ display: "block", height: width * FACE_ASPECT, width, ...sx }}
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d={FERNIE_SPROUT_PATH} fill={expression.sprout} />
			<g transform="translate(0 10)">
				<path d={FERNIE_TILE_PATH} fill={fernieColors.tile} />
				<g style={expression.glow ? { filter: EYE_GLOW } : undefined}>{expression.eyes}</g>
				{expression.mouth}
			</g>
		</Box>
	);
};

export default FernieFace;
